import { DataGrid, FilterToolbar, useNotifications } from '@ijoin/components';
import {
  GetAdminIraCompanionsRes,
  useDeleteAdminIraCompanion,
  useGetAdminIraCompanions
} from '@ijoin/hooks';
import { Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { createFileRoute } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useState } from 'react';
import { useToggle } from 'usehooks-ts';

import {
  IraCompanionActionsCell,
  IraCompanionFormDialog,
  IraCompanionPlansDialog,
  LinkButton
} from '../../components';
import { errorMessage } from '../../helpers';

const IraClarityPage = () => {
  const notifications = useNotifications();

  const [isCreateOpen, toggleCreateOpen] = useToggle();
  const [plansCompanionId, setPlansCompanionId] = useState<number>();

  const iraCompanions = useGetAdminIraCompanions();

  const deleteIraCompanion = useDeleteAdminIraCompanion({
    mutation: {
      onError: error =>
        notifications.error(
          errorMessage(error, 'Failed to delete IRA companion')
        ),
      onSuccess: () => {
        notifications.success('IRA companion deleted');
        iraCompanions.refetch();
      }
    }
  });

  const onClosePlans = () => setPlansCompanionId(undefined);

  const columns: GridColDef<GetAdminIraCompanionsRes>[] = [
    {
      field: 'planName',
      flex: 1.5,
      headerName: 'Name'
    },
    {
      field: 'planAssociationsCount',
      flex: 1,
      headerName: 'Plan Associations',
      renderCell: params => (
        <Button
          data-testid={`planAssociations-${camelCase(params.row.planName)}`}
          onClick={() => setPlansCompanionId(params.row.iraCompanionId)}
          variant='text'>
          {params.value}
        </Button>
      ),
      sortable: false
    },
    {
      field: 'rolloverProviderName',
      flex: 1,
      headerName: 'Rollover Provider'
    },
    {
      field: 'advisorName',
      flex: 1,
      headerName: 'Advisor'
    },
    {
      field: 'advisoryOrganizationName',
      flex: 1,
      headerName: 'Advisory Org',
      renderCell: params =>
        params.row.advisoryOrganizationId ? (
          <LinkButton
            data-testid={camelCase(params.row.advisoryOrganizationName ?? '')}
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId
            }}
            to='/organizations/$advisoryOrganizationId'
            variant='text'>
            {params.value}
          </LinkButton>
        ) : null
    },
    {
      align: 'center',
      display: 'flex',
      field: 'actions',
      headerAlign: 'center',
      headerName: '',
      renderCell: params => (
        <IraCompanionActionsCell
          onDelete={() =>
            deleteIraCompanion.mutate({
              iraCompanionId: params.row.iraCompanionId
            })
          }
          planAssociationsCount={params.row.planAssociationsCount}
          planName={params.row.planName}
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>IRA Clarity</Typography>
      <Tabs value='iraCompanions'>
        <Tab
          data-testid='tabIraCompanions'
          label='IRA Companions'
          value='iraCompanions'
        />
      </Tabs>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.iraCompanionId}
        initialSort={[{ field: 'planName', sort: 'asc' }]}
        loading={iraCompanions.isFetching}
        pageSizeOptions={[10, 25, 50, 100]}
        rows={iraCompanions.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          noRowsOverlay: { message: 'No IRA companions' },
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'iraCompanions'
          },
          toolbar: {
            addLabel: 'IRA Companion',
            addTestId: 'createIraCompanion',
            onAdd: toggleCreateOpen,
            onRefresh: iraCompanions.refetch
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sx={{ flex: 1, minHeight: 0 }}
      />

      {isCreateOpen && <IraCompanionFormDialog onClose={toggleCreateOpen} />}

      {plansCompanionId && (
        <IraCompanionPlansDialog
          iraCompanionId={plansCompanionId}
          onClose={onClosePlans}
        />
      )}
    </Stack>
  );
};

export const Route = createFileRoute('/ira-clarity/')({
  component: IraClarityPage
});
